import { createContext, useContext, useEffect, useMemo } from 'react';

import { useAccountsDataContext } from '#client/AccountsDataContext';
import { DB_APPLICATION_STATUS } from '#client/constants/application';
import useApplicationStatusContext from '#client/hooks/useApplicationStatusContext';
import { useLogError } from '#client/hooks/useLogError';
import useMfaStatus from '#client/hooks/useMfaStatus';
import useToastContext from '#client/hooks/useToastContext';

import useConstBusinessEntityUuid from './useConstBusinessEntityUuid';
import useSessionStorage from './useSessionStorage';

const APPLICATION_STATUS_WITH_UNIT_ACCOUNT_ID = [DB_APPLICATION_STATUS.APPROVED];

export const UnitAccountIdContext = createContext();
UnitAccountIdContext.displayName = 'UnitAccountIdContext';

const useUnitAccountId = () => {
  const context = useContext(UnitAccountIdContext);

  if (typeof context === 'undefined') {
    throw new Error('useUnitAccountId must be used within a UnitAccountIdContext.Provider');
  }

  return context;
};

export function UnitAccountIdProvider({ children }) {
  const { businessEntityUuid } = useConstBusinessEntityUuid();
  const [unitAccountId, setUnitAccountId] = useSessionStorage(
    `banking.unit_account_id.${businessEntityUuid}`,
    undefined
  );
  const { createCommonErrorToast } = useToastContext();

  const { appStatus } = useApplicationStatusContext();

  // I used useMfaStatus in UnitAccountIdProvider to avoid duplicate API calls
  const {
    isValidMfa,
    isEnabled,
    isEnrolled,
    isFetchingMfaStatus,
    error: mfaError
  } = useMfaStatus();

  const { loading, error, errors, data: unitAccountsData, refetch } = useAccountsDataContext();

  useLogError(errors, error);
  const parentAccountIds = unitAccountsData?.partnerAccounts?.partnerAccountIds;
  const partnerCustomerId = unitAccountsData?.bankingAccounts?.[0]?.partnerCustomerId ?? null;

  useEffect(() => {
    const unitAccountIsEmpty = parentAccountIds?.length === 0;
    if (parentAccountIds?.length > 0) {
      setUnitAccountId(parentAccountIds[0]);
    }

    if (
      unitAccountIsEmpty ||
      error ||
      (appStatus && !APPLICATION_STATUS_WITH_UNIT_ACCOUNT_ID.includes(appStatus))
    ) {
      setUnitAccountId(null);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentAccountIds, error, appStatus]);

  useEffect(() => {
    if (error) {
      createCommonErrorToast({ error });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const value = useMemo(() => {
    // If the application status is not allowed to have unit accounts,
    // or if the unitAccountId is null,
    // then consider the unit account ID as empty.
    const isEmptyUnitAccountId =
      (appStatus && !APPLICATION_STATUS_WITH_UNIT_ACCOUNT_ID.includes(appStatus)) ||
      unitAccountId === null;

    return {
      isValidMfa: !isFetchingMfaStatus && isValidMfa,
      isEnabled: !isFetchingMfaStatus && isEnabled,
      isEnrolled: !isFetchingMfaStatus && isEnrolled,
      mfaError,
      loading,
      error,
      unitAccountId,
      partnerCustomerId,
      isEmptyUnitAccountId,
      refetch,
      isFetchingMfaStatus
    };
  }, [
    error,
    appStatus,
    isEnabled,
    isEnrolled,
    isFetchingMfaStatus,
    isValidMfa,
    loading,
    mfaError,
    refetch,
    unitAccountId,
    partnerCustomerId
  ]);

  return <UnitAccountIdContext.Provider value={value}>{children}</UnitAccountIdContext.Provider>;
}

export default useUnitAccountId;
